/**
 * ET VARSEL SOM SETNING — det bjella i toppen tegner hver rad med.
 *
 * Flyttet hit fra `features/overview/NotificationsPanel.tsx` 24. oktober (276.10): bjella er det
 * eneste stedet man får varsler, så panelet på oversikten og fanen i innboksen er borte. Det som var
 * felles for de tre, står her.
 *
 * A NOTIFICATION IS A KEY AND A BAG OF PARAMETERS, NEVER A SENTENCE. The API stores `i18n_key`
 * and `i18n_params` and returns them untouched, so this component is where the sentence first
 * exists — `t(i18nKey, i18nParams)`, in whatever language the reader has now rather than the one
 * they had when the notification was written. `apps/api/src/notifications/i18n-keys.spec.ts` is
 * the other half of the split.
 */
import type {
  Actor,
  Locale,
  NotificationDto,
  OrganizationKind,
  RecordResource,
  Uuid,
} from '@tvitvi/shared';
import type { TFunction } from 'i18next';
import { Link } from '@tanstack/react-router';
import type { MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { NOTIFICATION_REASON_LABEL } from '@/features/inbox/follow-labels';
import { humanise } from '@/features/overview/ActivityLine';
import { currentLocale } from '@/i18n';
import { dynamicKey } from '@/i18n/types';
import { cn } from '@/lib/cn';
import { formatCalendarDate, formatDateTime, formatMoney } from '@/lib/format';

import { useNotificationRoute } from './use-notification-route';

/**
 * The resource `notification:update` is asked about — the caller's own feed.
 *
 * It has to be a RECORD, not a collection. `notification:update` is conditioned on `isSelf`,
 * which reads `subjectPersonId` off a record; handed a collection it falls through to
 * `not_creator` and the button disappears for everybody, superadmin included. That is not a
 * quirk to work around — it is why `apps/api/src/notifications/notification.resolver.ts` has a
 * `NotificationFeedResolver` at all, and this is the browser's copy of it, field for field.
 *
 * The subject is the caller, always. `r_notifications_recipient` is a RESTRICTIVE policy on
 * `recipient_person_id = app.require_person_id()` with no admin override, so every row this feed
 * can return is addressed to whoever asked for it — which is also why the DTO carries no
 * `recipientPersonId` to read it back from. No `notification:*` rule reads `id`, so this one
 * resource answers for the whole feed and for any single row in it, exactly as the API's does.
 *
 * EKSPORTERT 17. SEPTEMBER, fordi fella ble gått i igjen. KOI Del 4s varselfane spurte med en
 * COLLECTION, og fikk dermed `not_creator` for alle — «Merk alle som lest» og lesekontrollene på
 * hver rad fantes ikke for noen. Avsnittet over forutsa det ordrett, og det holdt ikke: så nå er
 * det den samme funksjonen framfor den samme kunnskapen skrevet to steder.
 */
export function notificationFeedResource(actor: Actor): RecordResource {
  return {
    kind: 'record',
    type: 'notification',
    id: actor.personId as string as Uuid,
    organizationId: actor.organizationId,
    subjectPersonId: actor.personId,
  };
}

/**
 * The notification itself: the stored key, rendered now, in this reader's language.
 *
 * The `defaultValue` is the event `type` — `task.assigned` → "task assigned". It is unreachable
 * while the API-side key spec holds, and it is what stands between a listener shipped ahead of
 * its locale entry and a raw `notifications.somethingNew.body` on somebody's dashboard.
 */
export function NotificationSentence({
  notification,
  kind,
  onNavigate,
  stretch = false,
}: {
  notification: NotificationDto;
  /** Huset som leser — en forespørsel åpnes forskjellige steder for festival og byrå. */
  kind: OrganizationKind;
  /**
   * Kalles NÅR MAN GÅR TIL OBJEKTET, ikke når raden vises.
   *
   * Klokka har merket raden lest ved aktivering siden første dag; varselfanen gjorde det bare med
   * flueben-knappen, så lista ble stående som en liste over hva man allerede hadde gjort (rettelse
   * B5). Den som merker lest er den som eier mutasjonen — derfor en callback framfor en spørring
   * inne i denne setningen.
   */
  onNavigate?: (event: MouseEvent<HTMLAnchorElement>) => void;
  /**
   * Lenka dekker hele raden (bjella, 276.10): et `::after` over den nærmeste `relative` forelderen,
   * så et klikk ved siden av setningen også går til objektet. Knappene i raden må stå over den.
   */
  stretch?: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const sentence = t(dynamicKey(notification.i18nKey), {
    ...notification.i18nParams,
    /* En frist er et tidspunkt og skrives som ett — se `instantParams`. */
    ...instantParams(notification.i18nParams, currentLocale()),
    ...calendarDateParams(notification.i18nParams, currentLocale()),
    ...moneyParams(notification.i18nParams, currentLocale()),
    /*
     * ONE PARAMETER IS ITSELF A KEY, AND IT HAS TO BE RESOLVED BEFORE IT IS SUBSTITUTED.
     *
     * `task.status_changed` carries `status: 'blocked'` — the enum VALUE, because a notification
     * stores i18n parameters and never rendered text (§6.20). Substituted raw it would put the
     * English column name into a Norwegian sentence: «Anders satte «Rigg scene» til blocked».
     *
     * `enums.task_status.*` is the same key the task list, the board and the status control all
     * render, so the word is the one the reader already knows — and there is exactly one place it
     * is translated. Every other parameter is a name or a title and passes through untouched.
     */
    ...statusParam(notification.i18nParams, t),
    /* F62: rollen er også en enum-verdi, og ordet er det medlemslista bruker. */
    ...roleParam(notification.i18nParams, t),
    defaultValue: humanise(notification.type.replace(/\./g, ' ')),
  });
  /*
   * SAMME MÅL SOM KLOKKA (QA5-B). Her sto en egen liste med tre typer — oppgave, tråd, kontakt —
   * så en møteinvitasjon eller en ny advance var klikkbar i klokka og død tekst i varselfanen og på
   * oversikten. `notificationRoute` er den ene regelen.
   */
  const target = useNotificationRoute(notification, kind);
  /*
   * VARSLER MED GRUNN (0931, 84): «Du er ansvarlig», «Du ble nevnt». Som GitHub sier hvorfor du får
   * et varsel — og det er den samme grunnen du følger tråden med.
   */
  const reason = notification.i18nParams['reason'];
  const reasonKey = typeof reason === 'string' ? NOTIFICATION_REASON_LABEL[reason] : undefined;
  const because =
    reasonKey === undefined ? null : (
      <span className="block text-xs text-muted" data-testid="notification-reason">
        {t(reasonKey)}
      </span>
    );
  if (target === null) {
    return (
      <>
        {sentence}
        {because}
      </>
    );
  }

  return (
    <>
      <Link
        to={target.to}
        {...(target.params === undefined ? {} : { params: target.params })}
        {...(target.search === undefined ? {} : { search: target.search })}
        className={cn(
          'ring-focus hover:text-accent-text hover:underline',
          stretch && "after:absolute after:inset-0 after:content-['']",
        )}
        onClick={onNavigate}
      >
        {sentence}
      </Link>
      {because}
    </>
  );
}

/**
 * The one notification parameter that is a KEY rather than a value.
 *
 * `task.status_changed` carries the enum value, so the word can be translated at read time in
 * whatever language the reader has NOW — the same reason the sentence itself is a key. This
 * resolves it through `enums.task_status.*`, which is where the task list, the board and the
 * status control all get the same word.
 *
 * Everything else passes through: a name is a name in both languages, and a task title is whatever
 * somebody typed.
 *
 * Exported for its own test. The failure it prevents is silent — a raw `blocked` in the middle of a
 * Norwegian sentence renders perfectly happily.
 */
/** F62: `role` i «Rollen din i … er nå …» — `enums.org_role.*`, som medlemslista. */
export function roleParam(
  params: NotificationDto['i18nParams'],
  t: TFunction,
): Record<string, string> {
  const role = params['role'];
  if (typeof role !== 'string' || role.length === 0) return {};
  return { role: t(dynamicKey(`enums.org_role.${role}`), { defaultValue: role }) };
}

export function statusParam(
  params: NotificationDto['i18nParams'],
  t: TFunction,
): Record<string, string> {
  const status = params['status'];
  if (typeof status !== 'string' || status.length === 0) return {};
  return {
    status: t(dynamicKey(`enums.task_status.${status}`), { defaultValue: status }),
  };
}

/** ISO 8601 med tidssone — formen `dueAt` og enhver annen frist lagres i. */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

/**
 * ET TIDSPUNKT SKRIVES SOM ET TIDSPUNKT, IKKE SOM DEN ISO-TEKSTEN DET ER LAGRET SOM.
 *
 * `task-notification.listener.ts` sier det ordrett om `dueAt`:
 *
 *   «Fristen går med som ISO-tekst, ikke som «i morgen kl. 14». Klienten formaterer den i leserens
 *    egen visning (…) så en varsling og en rad aldri kan si to forskjellige ting om det samme
 *    tidspunktet.»
 *
 * Klienten gjorde det ikke. Målt 17. september, på skjermbildet KOI-ordren ba om:
 *
 *   «Fristen for «Ring inn crew-liste til vaktselskapet» gikk ut 2026-09-17T18:00:00.000Z»
 *
 * En Z-suffikset maskinstreng midt i en norsk setning, i klokka, i varselfanen og på oversikten —
 * altså tre steder, fordi alle tre rendrer den samme setningen.
 *
 * FORMEN AVGJØR, IKKE NAVNET. Å slå opp på `dueAt` ville dekket den ene nøkkelen jeg så, og neste
 * lytter som sender en frist med et annet parameternavn ville hatt den samme feilen. En verdi som
 * ER et tidspunkt vises som et tidspunkt; alt annet går uendret gjennom. Et titteltekst-parameter
 * som tilfeldigvis er nøyaktig en ISO-tid er prisen, og det er en pris verdt å betale.
 *
 * Eksportert for sin egen test, som `statusParam` ved siden av og av den samme grunnen: feilen er
 * stille. Setningen rendres helt fint med maskinstrengen i seg.
 */
export function instantParams(
  params: NotificationDto['i18nParams'],
  locale: Locale,
): Record<string, string> {
  const formatted: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'string' && ISO_INSTANT.test(value)) {
      formatted[key] = formatDateTime(value, locale);
    }
  }
  return formatted;
}

/** En ren kalenderdag, `2027-08-12` — formen datoforespørslene og kveldene sender. */
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * EN DAG SKRIVES SOM EN DAG (QA G-4). Datoforespørslenes varsler sender datoen som `YYYY-MM-DD`
 * — riktig, fordi en dag ikke har tidssone — og setningen sto da med maskinformen midt i:
 * «Ny datoforespørsel fra Nordvind: Store sal, 2027-08-12». Den skrives her som en kalenderdato,
 * aldri som et tidspunkt, så den ikke flytter seg en dag i en annen sone.
 */
export function calendarDateParams(
  params: NotificationDto['i18nParams'],
  locale: Locale,
): Record<string, string> {
  const formatted: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'string' && ISO_DAY.test(value)) {
      formatted[key] = formatCalendarDate(value, locale);
    }
  }
  return formatted;
}

/** Øre og valuta, `12000000 NOK` — formen 253.80-varselet sender beløpene i. */
const MINOR_AND_CURRENCY = /^(-?\d+) ([A-Z]{3})$/;

/**
 * ET BELØP SKRIVES SOM PENGER (253.80). Varselet lagrer øre og valuta, aldri «120 000 kr», så
 * tallet skrives på leserens språk når det vises. Navnet MÅ ende på `Amount` i tillegg til formen,
 * så en tittel som tilfeldigvis er «2024 ABC» ikke blir til penger.
 */
export function moneyParams(
  params: NotificationDto['i18nParams'],
  locale: Locale,
): Record<string, string> {
  const formatted: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (!key.endsWith('Amount') || typeof value !== 'string') continue;
    const match = MINOR_AND_CURRENCY.exec(value);
    if (match?.[1] === undefined || match[2] === undefined) continue;
    formatted[key] = formatMoney(Number(match[1]), match[2], locale);
  }
  return formatted;
}
